import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import SignInSide from './components/SignInSide';
import SignUp from './components/SignUp';
import MainScrollingPage from './components/MainScrollingPage.jsx';
import BottomBar from './components/Navigation/BottomBar.jsx';
import TopBar from './components/Navigation/TopBar';
import ProtectedRoute from './components/ProtectedRoute';
import RoleSelection from './components/RoleSelection';
import CheckpointOnboarding from './components/Checkpoint/CheckpointOnboarding';
import ShadowLink from './components/ShadowLink';
import OnboardingFetcher from './components/Dashboard/Shared/OnboardingFetcher';
import NotAuthorizedComponent from './components/NotAuthorizedComponent';
import NotFoundComponent from './components/NotFoundComponent';
import ResolveInvitationUrl from './components/Dashboard/Shared/Invitation/ResolveInvitationUrl';
import AssessmentDetails from './components/Dashboard/Shared/Assessments/AssessmentDetails';
import ChatComponent from './components/ChatComponent';
import BuyerMakePayment from './components/Dashboard/Buyer/BuyerMakePayment';
import ForgotPassword from './components/Auth/ForgotPassword';


// Lazy-loaded components
const Marked = React.lazy(() => import('./components/Marked.jsx'));
const Messages = React.lazy(() => import('./components/Message/Messages'));
const BuyerDashboard = React.lazy(() => import('./components/Dashboard/Buyer/BuyerDashboard.jsx'));
const SellerDashboard = React.lazy(() => import('./components/Dashboard/Seller/SellerDashboard'));
const CheckpointDashboard = React.lazy(() => import('./components/Dashboard/Checkpoint/CheckpointDashboard'));
const AdminDashboard = React.lazy(() => import('./components/Dashboard/Admin/AdminDashboard'));
const PlatformDashboard = React.lazy(() => import('./components/Dashboard/Platform/PlatformDashboard'));
const GuestDashboard = React.lazy(() => import('./components/Dashboard/Guest/GuestDashboard'));
const AssessmentForm = React.lazy(() => import('./components/Dashboard/Shared/Assessments/AssessmentForm'));
const SendInvitation = React.lazy(() => import('./components/Dashboard/Shared/Invitation/SendInvitationPage'));
const ViewInvitationUrl = React.lazy(() => import('./components/Dashboard/Shared/Invitation/ViewInvitationUrl.jsx'));
const InstantBuy = React.lazy(() => import('./components/pages/Landing/InstantBuy'));
const InstantBuySummary = React.lazy(() => import('./components/pages/Landing/InstantBuySummary.jsx'));



// Seller Dashboard Lazy-loaded components
const MyShipments = React.lazy(() => import('./components/Dashboard/Seller/MyShipments'));
const PrepareShipment = React.lazy(() => import('./components/Dashboard/Seller/PrepareShipment'));
const ShipmentStatus = React.lazy(() => import('./components/Dashboard/Seller/ShipmentStatus'));
const InspectionReports = React.lazy(() => import('./components/Dashboard/Seller/InspectionReports'));
const DeliveryProgress = React.lazy(() => import('./components/Dashboard/Seller/DeliveryProgress'));
const Profile = React.lazy(() => import('./components/Dashboard/Seller/Profile'));
const Settings = React.lazy(() => import('./components/Dashboard/Seller/Settings'));

// Buyer Dashboard Lazy-loaded components
const MyOrders = React.lazy(() => import('./components/Dashboard/Buyer/MyOrders'));
const ParcelTracking = React.lazy(() => import('./components/Dashboard/Buyer/ParcelTracking'));
const InspectionReport = React.lazy(() => import('./components/Dashboard/Buyer/InspectionReport'));
const ConfirmDelivery = React.lazy(() => import('./components/Dashboard/Buyer/ConfirmDelivery'));
const BuyerProfile = React.lazy(() => import('./components/Dashboard/Buyer/BuyerProfile'));
const BuyerSettings = React.lazy(() => import('./components/Dashboard/Buyer/BuyerSettings'));



// Checkpoint Dashboard Lazy-loaded components
const ViewReports = React.lazy(() => import('./components/Dashboard/Checkpoint/ViewReports'));
const InspectProducts = React.lazy(() => import('./components/Dashboard/Checkpoint/InspectProducts'));
const ManageDelivery = React.lazy(() => import('./components/Dashboard/Checkpoint/ManageDelivery'));
const CheckpointProfile = React.lazy(() => import('./components/Dashboard/Checkpoint/CheckpointProfile'));
const CheckpointSettings = React.lazy(() => import('./components/Dashboard/Checkpoint/CheckpointSettings'));

const RoutesConfig = ({ onboardingData, setOnboardingData }) => {
  return (
    <>
      <Suspense fallback={<div>Loading TopBar...</div>}>
        <TopBar />
      </Suspense>
      <OnboardingFetcher onOnboardingFetched={setOnboardingData} />
      <div className="main-content">
        <Suspense fallback={<div>Loading content...</div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainScrollingPage />} />
            <Route path="/signin" element={<SignInSide />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/role-selection" element={<RoleSelection />} />
            <Route path="/checkpoint/checkpoint-onboarding" element={<CheckpointOnboarding />} />
            <Route path="/main-scrolling" element={<MainScrollingPage />} />
            <Route path="/marked" element={<ProtectedRoute element={<Marked />} />} />
            <Route path="/Messages" element={<ProtectedRoute element={<Messages />} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/instantbuy" element={<InstantBuy />} />
            <Route path="/summary" element={<InstantBuySummary />} />



            {/* Invitation Routes */}
            <Route path="/invitation" element={<ShadowLink />} />
            <Route path="/invite" element={<ViewInvitationUrl />} />
            <Route path="/resolve-invitation-url" element={<ResolveInvitationUrl />} />
            <Route path="/assessment/details" element={<AssessmentDetails />} />

            {/* Assessment Routes */}
            <Route path="/assessment/details" element={<AssessmentDetails />} />

            {/* Dashboard Routes */}
            <Route path="/dashboard/send-invitation" element={<ProtectedRoute element={<SendInvitation />} />} />
            <Route path="/dashboard/view-invitation" element={<ProtectedRoute element={<ViewInvitationUrl />} />} />

            {/* Buyer Dashboard */}
            <Route path="/buyer/dashboard" element={<ProtectedRoute element={<BuyerDashboard onboardingData={onboardingData} />} requiredRoles={['buyer']} />} />
            <Route path="/buyer/dashboard/assessment-form" element={<ProtectedRoute element={<AssessmentForm onboardingData={onboardingData} />} requiredRoles={['buyer']} />} />
            <Route path="/buyer/dashboard/send-invitation" element={<ProtectedRoute element={<SendInvitation />} requiredRoles={['buyer']} />} />
            <Route path="/buyer/dashboard/myorders" element={<ProtectedRoute element={<MyOrders />} requiredRoles={['buyer']} />} />
            {/*<Route path="/buyer/dashboard/parceltracking" element={<ProtectedRoute element={<ParcelTracking />} requiredRoles={['buyer']} />} />*/}
            {/*<Route path="/buyer/dashboard/inspectionreport" element={<ProtectedRoute element={<InspectionReport />} requiredRoles={['buyer']} />} />*/}
            {/*<Route path="/buyer/dashboard/confirmdelivery" element={<ProtectedRoute element={<ConfirmDelivery />} requiredRoles={['buyer']} />} />*/}
            <Route path="/buyer/dashboard/buyerprofile" element={<ProtectedRoute element={<BuyerProfile />} requiredRoles={['buyer']} />} />
            <Route path="/buyer/dashboard/buyersettings" element={<ProtectedRoute element={<BuyerSettings />} requiredRoles={['buyer']} />} />
            <Route path="/marked" element={<ProtectedRoute element={<Marked />} />} />
            <Route path="/Messages" element={<ProtectedRoute element={<Messages />} />} />
            <Route path="/Profile" element={<ProtectedRoute element={<Profile />} />} />
            <Route path="/myorders" element={<ProtectedRoute element={<MyOrders />} requiredRoles={['buyer', 'seller']} />} />
            <Route path="/buyer/dashboard/makepayment" element={<ProtectedRoute element={<BuyerMakePayment />} requiredRoles={['buyer', 'seller']} />} />
            <Route
  path="/buyer/dashboard/instantbuy"
  element={<ProtectedRoute element={<InstantBuy />} requiredRoles={['buyer']} />}
/>
<Route
  path="/buyer/dashboard/summary"
  element={<ProtectedRoute element={<InstantBuySummary />} requiredRoles={['buyer']} />}
/>

            {/* Seller Dashboard */}
            <Route path="/seller/dashboard" element={<ProtectedRoute element={<SellerDashboard onboardingData={onboardingData} />} requiredRoles={['seller']} />} />
            <Route path="/seller/dashboard/my-shipments" element={<ProtectedRoute element={<MyShipments />} requiredRoles={['seller']} />} />
            <Route path="/seller/dashboard/prepare-shipment" element={<ProtectedRoute element={<PrepareShipment />} requiredRoles={['seller']} />} />
            {/*<Route path="/seller/dashboard/shipment-status" element={<ProtectedRoute element={<ShipmentStatus />} requiredRoles={['seller']} />} />*/}
            {/*<Route path="/seller/dashboard/inspection-reports" element={<ProtectedRoute element={<InspectionReports />} requiredRoles={['seller']} />} />*/}
             {/*<Route path="/seller/dashboard/delivery-progress" element={<ProtectedRoute element={<DeliveryProgress />} requiredRoles={['seller']} />} />*/}
            <Route path="/seller/dashboard/profile" element={<ProtectedRoute element={<Profile />} requiredRoles={['seller']} />} />
            <Route path="/seller/dashboard/settings" element={<ProtectedRoute element={<Settings />} requiredRoles={['seller']} />} />
            <Route path="/seller/dashboard/AssessmentDetails/:assessmentId" element={<ProtectedRoute element={<AssessmentDetails />} requiredRoles={['seller']} />}/>
            <Route path="/seller/assessment/details" element={<ProtectedRoute element={<AssessmentDetails />} requiredRoles={['seller']} />} />
            <Route path="/checkpoint/checkpoint-onboarding" element={<ProtectedRoute element={<CheckpointOnboarding />} requiredRoles={['seller']} />} />
            <Route path="/marked" element={<ProtectedRoute element={<Marked />} />} />
            <Route path="/Messages" element={<ProtectedRoute element={<Messages />} />} />
            <Route path="/Profile" element={<ProtectedRoute element={<Profile />} />} />

            {/* Checkpoint Dashboard */}
            <Route path="/checkpoint/dashboard" element={<ProtectedRoute element={<CheckpointDashboard onboardingData={onboardingData} />} requiredRoles={['checkpoint']} />} />
            <Route path="/checkpoint/dashboard/reports" element={<ProtectedRoute element={<ViewReports />} requiredRoles={['checkpoint']} />} />
            <Route path="/checkpoint/dashboard/inspect" element={<ProtectedRoute element={<InspectProducts />} requiredRoles={['checkpoint']} />} />
            {/* <Route path="/checkpoint/dashboard/delivery" element={<ProtectedRoute element={<ManageDelivery />} requiredRoles={['checkpoint']} />} /> */}
            {/* <Route path="/checkpoint/dashboard/checkpointprofile" element={<ProtectedRoute element={<CheckpointProfile />} requiredRoles={['checkpoint']} /> */}
            {/* <Route path="/checkpoint/dashboard/checkpointsettings" element={<ProtectedRoute element={<CheckpointSettings />} requiredRoles={['checkpoint']} /> */}
            <Route path="/Profile" element={<ProtectedRoute element={<Profile />} />} />

            {/* Admin & Platform Dashboards */}
            <Route path="/admin/dashboard" element={<ProtectedRoute element={<AdminDashboard onboardingData={onboardingData} />} requiredRoles={['admin']} />} />
            <Route path="/platform/dashboard" element={<ProtectedRoute element={<PlatformDashboard onboardingData={onboardingData} />} requiredRoles={['platform']} />} />
            <Route path="/guest/dashboard" element={<ProtectedRoute element={<GuestDashboard onboardingData={onboardingData} />} />} />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFoundComponent />} />
            <Route path="/not-authorized" element={<NotAuthorizedComponent />} />
          </Routes>
        </Suspense>
      </div>
      <Suspense fallback={<div>Loading BottomBar...</div>}>
        <BottomBar />
      </Suspense>
    </>
  );
};

export default RoutesConfig;
